import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Alert, Box } from "@mui/material";

import { getSavedProjects } from "../../../../../store/actions/dataActions";
import {
  getCombineFields,
  buildPaisFieldTag,
  PAIS_COLUMN_DEFINITIONS,
} from "../../../../permitForms/permitFormHelpers";
import { categoryData as paisCategoryData } from "../../../../../data/pais/paisData";
import PaisTagAssignPopover from "./components/PaisTagAssignPopover";
import TableTagPdfPreview from "./components/TableTagPdfPreview";
import PreviewFormDialog from "./components/preview/PreviewFormDialog";
import { updatePreviewGroupInstanceLabel } from "./components/preview/previewFieldLabel";
import PDFEditorHeader from "./components/PDFEditorHeader";
import PDFEditorControlsBar from "./components/PDFEditorControlsBar";
import PDFEditorLeftPanel from "./components/PDFEditorLeftPanel";
import ObjectInspectorDrawer from "./components/ObjectInspectorDrawer";
import PDFCanvas from "./components/PDFCanvas";
import AddFieldDrawer from "./components/AddFieldDrawer";
import { usePdfEditorState } from "./hooks/usePdfEditorState";
import { useFormAutoSave } from "./hooks/useFormAutoSave";
import FormDetailsCard from "../components/FormDetails";
import {
  getDocumentDisplayRotation,
  getDocumentSize,
} from "./utils/documentOrientation";
import {
  buildVisibleTableRows,
  getTableSelectionMode,
} from "./utils/tableFieldLayout";
import { isFormEditLockLostError, renewFormEditLock } from "./formEditLockApi";
import FieldConnector from "./components/FieldConnector";
import { flattenFavouriteFields } from "./components/FavouritesBar";
import { getGenericPrefillError } from "../../../../../utils/genericFieldPrefill";
import ParcelAutoTagControl from "./components/ParcelAutoTagControl";
import { cleanupMultilineConnectionsAfterRetag } from "../../../../../utils/textFieldConnections";

const getEditorFieldKey = (field) =>
  field?.field_instance_id || field?.id || field?.name;

const isGenericTableMember = (field, tableId) =>
  field?.field_id === "generic_table" &&
  Boolean(field?.generic_table?.table_id) &&
  (!tableId || field.generic_table.table_id === tableId);

const getGenericTableColumnType = (field) =>
  field?.generic_table?.column_type ||
  field?.generic_table?.field_type ||
  "text";

const remapGenericTableFormulaReferences = (previousFields, nextFields) => {
  const nextFieldsByKey = new Map(
    nextFields.map((field) => [getEditorFieldKey(field), field]),
  );
  const renamedFields = new Map();

  previousFields.forEach((field, index) => {
    // Mapping preserves array order, including widgets without a stable ID.
    const nextField =
      field?.field_instance_id || field?.id
        ? nextFieldsByKey.get(getEditorFieldKey(field))
        : nextFields[index];

    if (
      field?.name &&
      nextField?.name &&
      field.name !== nextField.name &&
      (isGenericTableMember(field) || isGenericTableMember(nextField))
    ) {
      renamedFields.set(field.name, nextField);
    }
  });

  if (!renamedFields.size) return nextFields;

  return nextFields.map((field, index) => {
    if (
      !Array.isArray(field?.formulaFields) ||
      field.formulaFields !== previousFields[index]?.formulaFields
    ) {
      return field;
    }

    let changed = false;
    const formulaFields = field.formulaFields.map((step) => {
      const renamedField = renamedFields.get(step?.field?.name);
      if (!renamedField) return step;

      changed = true;
      return {
        ...step,
        field: {
          ...step.field,
          name: renamedField.name,
          field_label: renamedField.field_label,
        },
      };
    });

    return changed ? { ...field, formulaFields } : field;
  });
};

const PDFFormEditor = ({
  formData,
  onClose,
  onFinalizeSuccess,
  onSave,
  onNotify,
  isOpen = false,
  loading,
  isNew = false,
  onError,
  onSuccess,
  setFormData,
  jurisdictionOptions = [],
  finalizedEditMode = false,
  finalizedEditInitialView = "preview",
  externalEditLockLost = false,
  onRecoverEditLock,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const dispatch = useDispatch();

  const [objectInspectorOpen, setObjectInspectorOpen] = useState(false);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [blockPanelOpen, setBlockPanelOpen] = useState(true);
  const [editableTitle, setEditableTitle] = useState("");

  const [fields, setFields] = useState([]);
  const [fieldNavigationTarget, setFieldNavigationTarget] = useState(null);

  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [isLoadingUpdateSetting, setIsLoadingUpdateSetting] = useState(false);
  const [autoTagLoading, setAutoTagLoading] = useState(false);
  const [editLockLost, setEditLockLost] = useState(false);
  const titleFormKeyRef = useRef(null);
  const lockLossNotifiedRef = useRef(false);
  const lockRenewalPromiseRef = useRef(null);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const [paisPopoverOpen, setPaisPopoverOpen] = useState(false);
  const [savedPaisColumnOrder, setSavedPaisColumnOrder] = useState([]);
  const documentSize = getDocumentSize(formData);
  const displayRotation = getDocumentDisplayRotation(documentSize);

  const {
    currentProcessingFile,
    setCurrentProcessingFile,
    numPages,
    pageNumber,
    scale,
    pdfInput,
    base64File,
    resizing,
    setResizing,
    moving,
    setMoving,
    isDrawing,
    setIsDrawing,
    isSelecting,
    setIsSelecting,
    rect,
    setRect,
    selectionRect,
    setSelectionRect,
    freeSelectStartPoint,
    setFreeSelectStartPoint,
    startPoint,
    setStartPoint,
    initialMousePosition,
    setInitialMousePosition,
    initialInputSize,
    setInitialInputSize,
    inputPosition,
    setInputPosition,
    currCursor,
    setCurrCursor,
    resizeDirection,
    setResizeDirection,
    selectToolActive,
    typingToolActive,
    focusedField,
    setFocusedField,
    highlightFields,
    setHighlightFields,
    textOverlayDisplayMode,
    setTextOverlayDisplayMode,
    unassignedFieldNames,
    pdfContainerRef,
    pdfDocumentRef,
    inputRef,
    childInputRef,
    fixedInputs,
    handleFieldChange,
    onDocumentLoadSuccess,
    onPageLoadSuccess,
    handleScaleChange,
    handlePageChange,
    handleSave,
    hasUnsavedChanges,
    handleClose,
    activeEditorTool,
    handleEditorToolSelection,
    pdfScrollContainerRef,
    pushHistory,
    undo,
    redo,
    canRedo,
    canUndo,
    textSelectionMode,
    setTextSelectionMode,
  } = usePdfEditorState({
    formData,
    isOpen,
    onClose,
    onSave,
    onNotify,
    editableTitle,
    displayRotation,
  });

  const editorWidgetFields =
    currentProcessingFile?.document?.tempFields?.formFields;
  const prefillFormId =
    formData?.form_id || formData?.formId || formData?.form?.form_id || "";

  const handleBackgroundSave = useCallback(
    ({ signal, isCurrent }) =>
      handleSave(false, {
        background: true,
        silent: true,
        signal,
        isCurrent,
      }),
    [handleSave],
  );

  useFormAutoSave({
    enabled: Boolean(
      isOpen &&
        currentSession &&
        prefillFormId &&
        base64File &&
        numPages > 0 &&
        Array.isArray(editorWidgetFields) &&
        !formData?.skipPdfInit &&
        !editLockLost &&
        !externalEditLockLost &&
        typeof onSave === "function",
    ),
    documentKey: JSON.stringify([currentSession, prefillFormId]),
    hasUnsavedChanges,
    // Skip a tick during another operation or an unfinished pointer gesture.
    // These transient states must not restart the timer or cancel a save
    // already in progress when the user continues editing.
    isSaving: Boolean(
      loading ||
        isLoadingUpdateSetting ||
        autoTagLoading ||
        openSettingsDialog ||
        resizing ||
        moving ||
        isDrawing ||
        isSelecting,
    ),
    onSave: handleBackgroundSave,
  });

  const combineFields = React.useMemo(
    () => getCombineFields(editorWidgetFields || [], fields),
    [editorWidgetFields, fields],
  );

  const selectedTableRows = React.useMemo(() => {
    const tempFields = currentProcessingFile?.document?.tempFields || {};
    const widgetsByKey = new Map(
      (editorWidgetFields || []).map((field) => [
        getEditorFieldKey(field),
        field,
      ]),
    );

    const selection = tempFields.selectedExtraFields?.length
      ? tempFields.selectedExtraFields
      : tempFields.selectedFormField
        ? [tempFields.selectedFormField]
        : [];

    const selectedByKey = new Map();

    selection.forEach((selectedField) => {
      const field = widgetsByKey.get(getEditorFieldKey(selectedField));

      if (field && !field.name?.startsWith("block__")) {
        selectedByKey.set(getEditorFieldKey(field), field);
      }
    });

    const selectedFields = [...selectedByKey.values()];

    return {
      fields: selectedFields,
      rows: buildVisibleTableRows(
        selectedFields,
        tempFields.currentDocument || {},
      ),
    };
  }, [currentProcessingFile, editorWidgetFields]);

  const selectedTableMode = React.useMemo(
    () =>
      getTableSelectionMode(
        selectedTableRows.rows,
        selectedTableRows.fields.length,
      ),
    [selectedTableRows],
  );

  const editingGenericTable = React.useMemo(() => {
    const selectedFields = selectedTableRows.fields;
    const selectedTableIds = [
      ...new Set(
        selectedFields
          .filter((field) => isGenericTableMember(field))
          .map((field) => field.generic_table.table_id)
          .filter(Boolean),
      ),
    ];

    if (selectedTableIds.length !== 1) return null;

    const tableId = selectedTableIds[0];
    const selectedHasDifferentTable = selectedFields.some(
      (field) =>
        field?.generic_table?.table_id &&
        field.generic_table.table_id !== tableId,
    );

    if (selectedHasDifferentTable) return null;

    const currentTableFields = (editorWidgetFields || []).filter((field) =>
      isGenericTableMember(field, tableId),
    );

    if (currentTableFields.length === 0) return null;

    const candidateFieldsByKey = new Map(
      currentTableFields.map((field) => [getEditorFieldKey(field), field]),
    );

    selectedFields.forEach((field) => {
      const belongsToCurrentTable = isGenericTableMember(field, tableId);
      const isAvailableField = !isGenericTableMember(field);

      if (belongsToCurrentTable || isAvailableField) {
        candidateFieldsByKey.set(getEditorFieldKey(field), field);
      }
    });

    const candidateFields = [...candidateFieldsByKey.values()];
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};

    const candidateFieldsByPage = new Map();
    const rowGroups = new Map();

    // Preserve the saved grouping of existing table widgets.
    currentTableFields.forEach((field) => {
      const rowKey = `saved:${Number(field.generic_table.row_index)}`;

      if (!rowGroups.has(rowKey)) {
        rowGroups.set(rowKey, { fields: [] });
      }

      rowGroups.get(rowKey).fields.push(field);
    });

    // Detect PDF layout using existing AND newly selected fields together.
    candidateFields.forEach((field) => {
      const page = field.page_num ?? field.page ?? 1;

      if (!candidateFieldsByPage.has(page)) {
        candidateFieldsByPage.set(page, []);
      }

      candidateFieldsByPage.get(page).push(field);
    });

    const visibleRows = [...candidateFieldsByPage.entries()]
      .sort(([pageA], [pageB]) => Number(pageA) - Number(pageB))
      .flatMap(([, pageFields]) =>
        buildVisibleTableRows(pageFields, currentDocument),
      );

    const pdfOrderByFieldKey = new Map();
    let nextFieldOrder = 0;

    visibleRows.forEach((visibleRow, visibleRowIndex) => {
      visibleRow.fields.forEach((field) => {
        pdfOrderByFieldKey.set(getEditorFieldKey(field), nextFieldOrder++);
      });

      const existingRowKeys = [
        ...new Set(
          visibleRow.fields
            .filter((field) => isGenericTableMember(field, tableId))
            .map((field) => `saved:${Number(field.generic_table.row_index)}`),
        ),
      ];

      const newFields = visibleRow.fields.filter(
        (field) => !isGenericTableMember(field, tableId),
      );

      if (!newFields.length) return;

      if (existingRowKeys.length === 1) {
        // These widgets are beside an existing row: add them as columns.
        rowGroups.get(existingRowKeys[0]).fields.push(...newFields);
      } else {
        // No unambiguous existing row: retain a separate new row.
        rowGroups.set(`new:${visibleRowIndex}`, {
          fields: newFields,
        });
      }
    });

    const getFieldPdfOrder = (field) =>
      pdfOrderByFieldKey.get(getEditorFieldKey(field)) ??
      Number.MAX_SAFE_INTEGER;

    const candidateRows = [...rowGroups.values()]
      .map((row) => ({
        ...row,
        fields: [...row.fields].sort(
          (fieldA, fieldB) =>
            getFieldPdfOrder(fieldA) - getFieldPdfOrder(fieldB),
        ),
      }))
      .sort(
        (rowA, rowB) =>
          getFieldPdfOrder(rowA.fields[0]) - getFieldPdfOrder(rowB.fields[0]),
      );

    // Share the candidate column positions with removal and saving.
    const candidateColumnIndexByFieldKey = new Map();

    candidateRows.forEach((row) => {
      row.fields.forEach((field, columnIndex) => {
        candidateColumnIndexByFieldKey.set(
          getEditorFieldKey(field),
          columnIndex,
        );
      });
    });

    const columnCount = candidateRows.reduce(
      (max, row) => Math.max(max, row.fields.length),
      0,
    );
    const firstMetadata = currentTableFields[0]?.generic_table || {};
    const rowNames = candidateRows.map((row) => {
      const existingRowField = row.fields.find(
        (field) => field?.generic_table?.table_id === tableId,
      );

      return existingRowField?.generic_table?.row_name || "";
    });
    const existingFieldAtColumn = (columnIndex) =>
      currentTableFields.find(
        (field) =>
          candidateColumnIndexByFieldKey.get(getEditorFieldKey(field)) ===
          columnIndex,
      );

    const columnNames = Array.from(
      { length: columnCount },
      (_, columnIndex) =>
        existingFieldAtColumn(columnIndex)?.generic_table?.column_name || "",
    );

    const columnTypes = Array.from({ length: columnCount }, (_, columnIndex) =>
      getGenericTableColumnType(existingFieldAtColumn(columnIndex)),
    );

    const candidateColumnBySavedIndex = new Map();
    currentTableFields.forEach((field) => {
      const savedIndex = Number(field.generic_table.column_index);
      const candidateIndex = candidateColumnIndexByFieldKey.get(
        getEditorFieldKey(field),
      );
      if (!candidateColumnBySavedIndex.has(savedIndex)) {
        candidateColumnBySavedIndex.set(savedIndex, candidateIndex);
      }
    });
    const columnTotalSources = Array.from(
      { length: columnCount },
      (_, columnIndex) => {
        const sources =
          existingFieldAtColumn(columnIndex)?.generic_table
            ?.column_total_sources;

        return Array.isArray(sources)
          ? sources
              .map((index) => candidateColumnBySavedIndex.get(index))
              .filter((index) => Number.isInteger(index))
          : [];
      },
    );

    return {
      tableId,
      columnIndexByFieldKey: candidateColumnIndexByFieldKey,
      candidateFieldKeys: candidateFields.map(getEditorFieldKey),
      rows: candidateRows,
      fields: candidateFields,
      config: {
        label: firstMetadata.table_label || "",
        rowMode: firstMetadata.row_mode || "fixed",
        headerMode: firstMetadata.header_mode || "named_rows",
        rowCount: candidateRows.length,
        maxRowCount: firstMetadata.max_row_count ?? null,
        columnCount,
        rowNames,
        columnNames,
        columnTypes,
        columnTotalSources,
      },
    };
  }, [
    currentProcessingFile?.document?.tempFields?.currentDocument,
    editorWidgetFields,
    selectedTableRows.fields,
  ]);

  const showPaisTableButton = React.useMemo(() => {
    if (activeEditorTool !== "edit") {
      return false;
    }

    return (
      selectedTableMode.supportsGenericTableTagging ||
      Boolean(editingGenericTable)
    );
  }, [activeEditorTool, editingGenericTable, selectedTableMode]);

  const tableTagSelectionMode = React.useMemo(() => {
    if (activeEditorTool !== "edit") {
      return {
        supportsPaisTableTagging: false,
        supportsGenericTableTagging: false,
      };
    }

    if (editingGenericTable) {
      return {
        supportsPaisTableTagging: false,
        supportsGenericTableTagging: true,
      };
    }

    return selectedTableMode;
  }, [activeEditorTool, editingGenericTable, selectedTableMode]);

  const paisSelectedTableRows = selectedTableRows.rows;

  const paisDetectedColumnCount = React.useMemo(
    () =>
      paisSelectedTableRows.reduce(
        (max, row) => Math.max(max, row.fields?.length || 0),
        0,
      ),
    [paisSelectedTableRows],
  );

  const canUnassignSelectedFields = React.useMemo(() => {
    const selectedFields = (
      currentProcessingFile?.document?.tempFields?.selectedExtraFields || []
    ).filter((field) => !field?.name?.startsWith("block__"));

    return (
      selectedFields.length > 1 &&
      selectedFields.every((field) => Boolean(field?.field_id))
    );
  }, [currentProcessingFile]);

  const handleUnassignSelectedFields = useCallback(() => {
    const selectedFields = (
      currentProcessingFile?.document?.tempFields?.selectedExtraFields || []
    ).filter((field) => !field?.name?.startsWith("block__"));

    if (
      selectedFields.length < 2 ||
      selectedFields.some((field) => !field?.field_id)
    ) {
      return;
    }

    const getFieldKey = (field) =>
      field?.field_instance_id || field?.id || field?.name;
    const selectedFieldKeys = new Set(selectedFields.map(getFieldKey));

    pushHistory?.();
    setCurrentProcessingFile((prevFile) => {
      const tempFields = prevFile?.document?.tempFields || {};

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...tempFields,
            formFields: (tempFields.formFields || []).map((field) => {
              if (!selectedFieldKeys.has(getFieldKey(field))) return field;

              const { generic_table, ...untaggedField } = field;

              return {
                ...untaggedField,
                field_id: "",
                location: "",
                prefill: null,
              };
            }),
            selectedFormField: null,
            selectedExtraFields: [],
          },
        },
      };
    });
  }, [currentProcessingFile, pushHistory, setCurrentProcessingFile]);

  const getFormFields = useCallback(async () => {
    try {
      await dispatch(getSavedProjects());

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
        { currentSession },
        { withCredentials: true },
      );

      if (res.data.success) {
        const fields = res.data.data?.fields;
        setFields(fields);
      }
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  useEffect(() => {
    if (externalEditLockLost) {
      setEditLockLost(true);
    }
  }, [externalEditLockLost]);

  useEffect(() => {
    getFormFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigateToTaggedField = useCallback(
    (taggedField) => {
      if (!taggedField?.field_id) return;

      const findFieldById = (fieldId, fieldList = []) => {
        for (const field of fieldList) {
          if (field?.field_id === fieldId) {
            return field;
          }

          const childMatch = findFieldById(fieldId, field?.children || []);
          if (childMatch) return childMatch;
        }

        return null;
      };

      const assignedField = findFieldById(taggedField.field_id, fields);
      const parentField = assignedField?.parent_field_id
        ? findFieldById(assignedField.parent_field_id, fields)
        : null;
      const navigationField = parentField || assignedField;

      setBlockPanelOpen(true);
      setFieldNavigationTarget({
        fieldId: navigationField?.field_id || taggedField.field_id,
        searchQuery:
          navigationField?.field_name || navigationField?.field_label || "",
        tabValue: navigationField?.is_default ? "3" : "2",
        requestId: Date.now(),
      });
    },
    [fields],
  );

  const handleFieldNavigationComplete = useCallback((requestId) => {
    setFieldNavigationTarget((currentTarget) =>
      currentTarget?.requestId === requestId ? null : currentTarget,
    );
  }, []);

  const handleSearchSelectedText = useCallback((selectedText) => {
    const searchQuery = selectedText?.replace(/\s+/g, " ").trim();
    if (!searchQuery) return;

    setBlockPanelOpen(true);
    setFieldNavigationTarget({
      searchQuery,
      tabValue: "2",
      requestId: Date.now(),
      searchOnly: true,
    });
  }, []);

  const handleFavouriteFieldSelect = useCallback(
    (field, _favourite, targetWidgetName) => {
      if (loading || autoTagLoading || editLockLost) return;
      if (!field?.field_id) return;

      const tempFields = currentProcessingFile?.document?.tempFields;

      const widgets = tempFields?.formFields || [];

      let widgetName = targetWidgetName;

      // Clicking uses the current selection.
      // Dropping supplies the destination widget directly.
      if (!widgetName) {
        const extraFields = tempFields?.selectedExtraFields || [];

        const selectedFields = extraFields.length
          ? extraFields
          : tempFields?.selectedFormField
            ? [tempFields.selectedFormField]
            : [];

        if (selectedFields.length !== 1) {
          onNotify?.(
            "error",
            selectedFields.length === 0
              ? "Select a PDF widget, or drag a favorite onto one."
              : "Select only one PDF widget before clicking a favorite.",
          );
          return;
        }

        widgetName = selectedFields[0]?.name;
      }

      const targetWidget = widgets.find((widget) => widget.name === widgetName);

      if (
        !targetWidget ||
        widgetName.startsWith("block__") ||
        !["PDFTextField", "PDFText", "PDFCheckBox", "PDFRadioGroup"].includes(
          targetWidget.type,
        )
      ) {
        onNotify?.("error", "Choose a PDF field widget to assign.");
        return;
      }

      const assignment = {
        field_id: field.field_id,
        field_label:
          field.field_label ||
          field.label ||
          field.field_name ||
          field.name ||
          "",
        location: field.location || "create",
        ...(field.field_type === "percentageCheckbox" ? { hidden: false } : {}),
      };

      const alreadyAssigned = Object.entries(assignment).every(
        ([key, value]) => targetWidget[key] === value,
      );

      if (alreadyAssigned) return;

      pushHistory?.();

      setCurrentProcessingFile((previous) => {
        const previousTemp = previous?.document?.tempFields;

        const currentWidget = previousTemp?.formFields?.find(
          (widget) => widget.name === widgetName,
        );

        if (!currentWidget) return previous;

        const assignedWidget = {
          ...currentWidget,
          ...assignment,
        };

        return cleanupMultilineConnectionsAfterRetag(previous, {
          ...previous,
          document: {
            ...previous.document,
            tempFields: {
              ...previousTemp,
              formFields: previousTemp.formFields.map((widget) =>
                widget.name === widgetName ? assignedWidget : widget,
              ),
              selectedFormField: assignedWidget,
              selectedExtraFields: [assignedWidget],
            },
          },
        });
      });
    },
    [
      currentProcessingFile,
      loading,
      autoTagLoading,
      editLockLost,
      onNotify,
      pushHistory,
      setCurrentProcessingFile,
    ],
  );
  const favouriteSignerContext = React.useMemo(
    () =>
      formData?.updated_settings?.signatures ??
      formData?.guessedFormDetails?.signatures ??
      {},
    [
      formData?.updated_settings?.signatures,
      formData?.guessedFormDetails?.signatures,
    ],
  );

  const handleFavouriteDrop = useCallback(
    (fieldId, widgetName) => {
      const field = flattenFavouriteFields(fields).find(
        (item) => String(item.field_id) === String(fieldId),
      );

      if (!field) {
        onNotify?.(
          "error",
          "This favorite's form field is no longer available.",
        );
        return;
      }

      handleFavouriteFieldSelect(field, undefined, widgetName);
    },
    [fields, handleFavouriteFieldSelect, onNotify],
  );

  React.useEffect(() => {
    if (!isOpen) {
      titleFormKeyRef.current = null;
      return;
    }

    if (!formData) return;

    const titleFormKey =
      formData?.form_id ||
      formData?.formId ||
      formData?.form?.form_id ||
      formData?.fileName ||
      "new-form";

    if (titleFormKeyRef.current === titleFormKey) return;

    titleFormKeyRef.current = titleFormKey;
    setEditableTitle(
      formData?.guessedFormDetails?.label_guess ||
        formData?.formName ||
        formData?.fileName ||
        "Untitled Form",
    );
  }, [formData, isOpen]);

  const finalizedEditorReady = Boolean(currentProcessingFile);

  useEffect(() => {
    if (!isOpen) {
      setOpenPreviewDialog(false);
      setOpenSettingsDialog(false);
      return;
    }

    if (!finalizedEditMode || !finalizedEditorReady) return;

    const shouldOpenSettings = finalizedEditInitialView === "settings";
    setOpenPreviewDialog(!shouldOpenSettings);
    setOpenSettingsDialog(shouldOpenSettings);
  }, [
    finalizedEditInitialView,
    finalizedEditMode,
    finalizedEditorReady,
    isOpen,
  ]);

  const editLockFormId = formData?.editLock?.formId || "";
  const editLockId = formData?.editLock?.lockId || "";
  const editLockHeartbeatSeconds = formData?.editLock?.heartbeatSeconds || 30;

  const renewCurrentEditLock = useCallback(
    async ({ attempts = 3 } = {}) => {
      if (!editLockFormId || !editLockId) return true;
      if (lockRenewalPromiseRef.current) return lockRenewalPromiseRef.current;

      const renewalPromise = renewFormEditLock({
        currentSession,
        formId: editLockFormId,
        lockId: editLockId,
        attempts,
      })
        .then(() => true)
        .catch(async (error) => {
          if (isFormEditLockLostError(error)) {
            if (onRecoverEditLock) {
              try {
                const recovered = await onRecoverEditLock();
                if (recovered) return recovered;
              } catch (recoveryError) {
                error = recoveryError;
              }
            }
            setEditLockLost(true);
            if (!lockLossNotifiedRef.current) {
              lockLossNotifiedRef.current = true;
              onErrorRef.current?.(
                error?.response?.data?.message ||
                  "The editor lock was lost. Saving has been disabled; close and reopen the form.",
              );
            }
          } else {
            console.warn(
              "Temporary form editor lock heartbeat failure:",
              error,
            );
          }
          return false;
        })
        .finally(() => {
          if (lockRenewalPromiseRef.current === renewalPromise) {
            lockRenewalPromiseRef.current = null;
          }
        });

      lockRenewalPromiseRef.current = renewalPromise;
      return renewalPromise;
    },
    [currentSession, editLockFormId, editLockId, onRecoverEditLock],
  );

  useEffect(() => {
    if (!isOpen || !editLockFormId || !editLockId) {
      setEditLockLost(false);
      lockLossNotifiedRef.current = false;
      return undefined;
    }

    const heartbeat = () => {
      renewCurrentEditLock();
    };

    heartbeat();
    const interval = window.setInterval(
      heartbeat,
      Math.max(5, Number(editLockHeartbeatSeconds) || 30) * 1000,
    );

    const handleOnline = () => heartbeat();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") heartbeat();
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    editLockFormId,
    editLockHeartbeatSeconds,
    editLockId,
    isOpen,
    renewCurrentEditLock,
  ]);

  useEffect(() => {
    if (!showPaisTableButton) {
      setPaisPopoverOpen(false);
    }
  }, [showPaisTableButton]);

  const handleOpenPaisPopover = useCallback(() => {
    if (!showPaisTableButton) {
      return;
    }

    setPaisPopoverOpen(true);
  }, [showPaisTableButton]);

  const handleRemoveHiddenFieldFromSelection = () => {
    setCurrentProcessingFile((prevFile) => {
      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedExtraFields:
              prevFile.document.tempFields.selectedExtraFields.filter(
                (f) => !f?.hidden,
              ),
            selectedFormField: prevFile.document.tempFields.selectedFormField
              ?.hidden
              ? null
              : prevFile.document.tempFields.selectedFormField,
          },
        },
      };
    });
  };

  const handleToggleObjectInspector = () => {
    if (addFieldOpen && !objectInspectorOpen) {
      setAddFieldOpen(false);
    }
    if (objectInspectorOpen) {
      handleRemoveHiddenFieldFromSelection();
    }
    setObjectInspectorOpen((prev) => !prev);
  };

  const handleToggleAddField = () => {
    if (objectInspectorOpen) {
      handleRemoveHiddenFieldFromSelection();
    }
    if (objectInspectorOpen && !addFieldOpen) {
      setObjectInspectorOpen(false);
    }
    setAddFieldOpen((prev) => !prev);
  };

  const handleUpdateSettings = async (settings) => {
    const formId = formData?.formId || formData?.form?.form_id;

    if (!formId) {
      onError("No form ID found.");
      return;
    }

    if (!settings) {
      onError("Settings data is missing.");
      return;
    }

    setIsLoadingUpdateSetting(true);

    try {
      const renewedLock = await renewCurrentEditLock();
      if (!renewedLock) {
        if (!editLockLost) {
          onErrorRef.current?.(
            "The edit session could not be confirmed because of a connection problem. Your changes remain in the editor; check your connection and try again.",
          );
        }
        return;
      }

      const { documentLabel, ...documentSettings } = settings;
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/update-settings`,
        {
          currentSession,
          formId,
          settings: documentSettings,
          syncedFormEditToken: formData?.syncedFormEditToken || "",
          lockId: renewedLock?.lockId || formData?.editLock?.lockId || "",
        },
        { withCredentials: true },
      );

      if (!response?.data?.success) {
        onError(response?.data?.message || "Failed to update form settings");
        return;
      }

      const updatedSettings = response?.data?.data?.updated_settings || {};
      const nextDocumentLabel = documentLabel?.trim();

      if (nextDocumentLabel && nextDocumentLabel !== editableTitle) {
        const labelUpdated = await handleSave(false, {
          title: nextDocumentLabel,
          silent: true,
        });

        if (!labelUpdated) return;
        setEditableTitle(nextDocumentLabel);
      }

      setFormData((prev) => ({
        ...(prev || {}),
        ...(response?.data?.data || {}),
        settings: {
          ...(prev?.settings || {}),
          ...updatedSettings,
        },
        ...(documentSettings?.permitType
          ? {
              selected_permit_type: documentSettings.permitFormId
                ? documentSettings.permitType
                : "",
            }
          : {}),
      }));

      onSuccess("Form settings updated successfully");
      if (finalizedEditMode) {
        if (onFinalizeSuccess) {
          await onFinalizeSuccess();
        } else {
          handleClose();
        }
      } else {
        setOpenSettingsDialog(false);
      }
    } catch (error) {
      console.error("Form settings update error:", error);
      if (
        error?.response?.status === 409 &&
        error?.response?.data?.code === "FORM_EDIT_LOCK_LOST"
      ) {
        setEditLockLost(true);
      }
      onError(
        error?.response?.data?.message ||
          "Failed to update form settings. Please try again.",
      );
    } finally {
      setIsLoadingUpdateSetting(false);
    }
  };

  const editorBusy = loading || autoTagLoading || editLockLost;

  const handleAutoTagFields = useCallback(async () => {
    const formId =
      formData?.form_id || formData?.formId || formData?.form?.form_id || "";
    const instances =
      currentProcessingFile?.document?.tempFields?.formFields || [];

    if (!formId) {
      onError?.("No form ID found.");
      return;
    }

    try {
      setAutoTagLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-instances/auto-tag-fields`,
        {
          form_id: formId,
          instances,
        },
        { withCredentials: true },
      );

      if (!response?.data?.success) {
        onError?.(response?.data?.message || "Failed to auto tag fields");
        return;
      }

      const matchedInstances = response?.data?.data?.matched || [];

      if (!Array.isArray(matchedInstances)) {
        onError?.("Invalid auto tag response received.");
        return;
      }

      if (matchedInstances.length === 0) {
        onSuccess?.("Auto tag completed successfully");
        return;
      }

      setCurrentProcessingFile((prevFile) => {
        if (!prevFile?.document?.tempFields) return prevFile;

        const selectedField = prevFile.document.tempFields.selectedFormField;
        const selectedExtras =
          prevFile.document.tempFields.selectedExtraFields || [];

        const matchedById = new Map(
          matchedInstances
            .filter((field) => field?.field_instance_id)
            .map((field) => [field.field_instance_id, field]),
        );
        const normalizedInstances = (
          prevFile.document.tempFields.formFields || []
        ).map((field) => {
          const matchedField = matchedById.get(field?.field_instance_id);

          if (!matchedField) {
            return field?.type === "PDFTextField"
              ? { ...field, autoFontSize: true }
              : field;
          }

          const mergedField = {
            ...field,
            ...matchedField,
            field_id: matchedField.field_id,
            field_label: matchedField.matched_to,
            label: matchedField.matched_to,
            name: field.name,
          };

          return mergedField?.type === "PDFTextField"
            ? { ...mergedField, autoFontSize: true }
            : mergedField;
        });

        const matchField = (candidate) =>
          normalizedInstances.find((field) =>
            candidate?.field_instance_id
              ? field?.field_instance_id === candidate.field_instance_id
              : field?.name === candidate?.name,
          );

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: normalizedInstances,
              selectedFormField: matchField(selectedField) || null,
              selectedExtraFields: selectedExtras
                .map((field) => matchField(field))
                .filter(Boolean),
            },
          },
        };
      });

      onSuccess?.("Auto tag completed successfully");
    } catch (error) {
      console.error("Auto tag fields error:", error);
      onError?.(
        error?.response?.data?.message ||
          "Failed to auto tag fields. Please try again.",
      );
    } finally {
      setAutoTagLoading(false);
    }
  }, [
    currentProcessingFile,
    formData,
    onError,
    onSuccess,
    setCurrentProcessingFile,
  ]);

  // Prefill targets are document-scoped instances, never generic field IDs or names.
  // Keep this separate from the existing inspector updates and their matching rules.
  const handleObjectInspectorPrefillChange = useCallback(
    (prefill, { formId, targetInstanceId } = {}) => {
      const tempFields = currentProcessingFile?.document?.tempFields;
      if (
        editorBusy ||
        formId !== prefillFormId ||
        tempFields?.selectedPdfTextItem ||
        tempFields?.selectedExtraFields?.length > 1 ||
        tempFields?.selectedFormField?.field_instance_id !== targetInstanceId
      ) {
        return false;
      }

      const error = getGenericPrefillError({
        formId,
        fields: tempFields?.formFields || [],
        targetInstanceId,
        prefill,
      });
      if (error) {
        onNotify?.("error", error);
        return false;
      }

      // Persist only the agreed configuration, without source display labels.
      const nextPrefill =
        prefill === null
          ? null
          : prefill.mode === "manual"
            ? { mode: "manual", value: prefill.value }
            : {
                mode: "field",
                source_field_id: prefill.source_field_id,
                ...(prefill.source_field_instance_id != null
                  ? {
                      source_field_instance_id:
                        prefill.source_field_instance_id,
                    }
                  : {}),
              };
      const isTarget = (field) =>
        field?.field_instance_id === targetInstanceId &&
        (field.form_id == null || field.form_id === formId);
      const target = tempFields.formFields.find(isTarget);
      if (
        JSON.stringify(target.prefill ?? null) === JSON.stringify(nextPrefill)
      ) {
        return true;
      }

      pushHistory?.();
      setCurrentProcessingFile((previous) => {
        // A deferred inspector must not write into a newly selected document/widget.
        if (previous !== currentProcessingFile) return previous;

        const updateTarget = (field) =>
          isTarget(field) ? { ...field, prefill: nextPrefill } : field;

        return {
          ...previous,
          document: {
            ...previous.document,
            tempFields: {
              ...tempFields,
              formFields: tempFields.formFields.map(updateTarget),
              selectedFormField: updateTarget(tempFields.selectedFormField),
              ...(Array.isArray(tempFields.selectedExtraFields)
                ? {
                    selectedExtraFields:
                      tempFields.selectedExtraFields.map(updateTarget),
                  }
                : {}),
            },
          },
        };
      });
      return true;
    },
    [
      currentProcessingFile,
      editorBusy,
      prefillFormId,
      onNotify,
      pushHistory,
      setCurrentProcessingFile,
    ],
  );

  const handleObjectInspectorFieldChange = useCallback(
    (fieldKey, value) => {
      pushHistory?.();
      setCurrentProcessingFile((prev) => {
        const selectedTextItem =
          prev?.document?.tempFields?.selectedPdfTextItem || null;
        if (selectedTextItem) {
          const existingEdits = prev.document.tempFields.pdfTextEdits || [];
          const existingEdit = existingEdits.find(
            (edit) => edit.target_item_id === selectedTextItem.id,
          );

          let nextEdit = existingEdit
            ? { ...existingEdit }
            : {
                edit_id: `text_edit_${Date.now()}`,
                target_item_id: selectedTextItem.id,
                page: selectedTextItem.page,
                original_text: selectedTextItem.text,
                original_rect: { ...selectedTextItem.rect },
                rect: { ...selectedTextItem.rect },
                action: "replace",
                new_text: selectedTextItem.text,
              };

          if (fieldKey === "text") {
            nextEdit.new_text = value;
            nextEdit.action = value === "" ? "delete" : "replace";
          } else if (fieldKey === "action") {
            nextEdit.action = value;
          } else if (["x", "y", "width", "height"].includes(fieldKey)) {
            const rectKeyMap = { x: "X", y: "Y", width: "W", height: "H" };
            nextEdit.rect = {
              ...(nextEdit.rect || { ...selectedTextItem.rect }),
              [rectKeyMap[fieldKey]]: Number(value),
            };
            if (nextEdit.action !== "delete") {
              nextEdit.action = nextEdit.new_text ? "move" : "replace";
            }
          }

          const nextEdits = existingEdit
            ? existingEdits.map((edit) =>
                edit.target_item_id === selectedTextItem.id ? nextEdit : edit,
              )
            : [...existingEdits, nextEdit];

          return {
            ...prev,
            document: {
              ...prev.document,
              tempFields: {
                ...prev.document.tempFields,
                pdfTextEdits: nextEdits,
              },
            },
          };
        }

        if (!prev?.document?.tempFields?.selectedFormField) return prev;

        const selectedField = prev.document.tempFields.selectedFormField;
        const selectedFieldName = selectedField?.name;
        const selectedFieldId = selectedField?.id;

        if (fieldKey === "__group_label__") {
          if (!selectedField?.field_id) return prev;

          const group = combineFields?.find(
            (field) =>
              ["checkboxGroup", "selectGroup"].includes(field?.field_type) &&
              (field.field_id === selectedField.field_id ||
                field.options?.some(
                  (option) => option.field_id === selectedField.field_id,
                )),
          );
          if (!group) return prev;

          const tempFields = prev.document.tempFields;
          const formFields = tempFields.formFields || [];
          const updatedFormFields = updatePreviewGroupInstanceLabel(
            formFields,
            group,
            value,
          );
          if (updatedFormFields === formFields) return prev;

          const findUpdatedField = (field) =>
            updatedFormFields.find(
              (candidate) =>
                getEditorFieldKey(candidate) === getEditorFieldKey(field),
            ) || field;

          return {
            ...prev,
            document: {
              ...prev.document,
              tempFields: {
                ...tempFields,
                formFields: updatedFormFields,
                selectedFormField: findUpdatedField(selectedField),
                ...(Array.isArray(tempFields.selectedExtraFields)
                  ? {
                      selectedExtraFields:
                        tempFields.selectedExtraFields.map(findUpdatedField),
                    }
                  : {}),
              },
            },
          };
        }

        if (fieldKey === "name") {
          const normalizedValue = String(value || "").trim();
          const duplicateField = (
            prev.document.tempFields.formFields || []
          ).find((field) => {
            const isSameField = selectedFieldId
              ? field?.id === selectedFieldId
              : field?.name === selectedFieldName;

            return (
              !isSameField && String(field?.name || "") === normalizedValue
            );
          });

          if (duplicateField) {
            onNotify?.(
              "error",
              "Field name already exists. Names must be unique across all pages.",
            );
            return prev;
          }

          value = normalizedValue;
        }

        const relatedOptionFields = combineFields
          ?.filter((f) =>
            ["selectGroup", "checkboxGroup"].includes(f?.field_type),
          )
          .find((field) =>
            field.options?.some((opt) => {
              if (opt.field_id === selectedField?.field_id) {
                return true;
              }
              return false;
            }),
          )
          ?.options?.map((opt) => (selectedFieldId ? opt?.id : opt?.name));

        const genericCellChanges = isGenericTableMember(selectedField)
          ? fieldKey === "field_type" &&
            ["text", "number", "total"].includes(value)
            ? {
                generic_table: {
                  ...selectedField.generic_table,
                  column_type: getGenericTableColumnType(selectedField),
                  field_type: value,
                  field_type_override: value,
                  formula_override: true,
                },
              }
            : fieldKey === "formulaFields"
              ? {
                  generic_table: {
                    ...selectedField.generic_table,
                    formula_override: true,
                  },
                }
              : {}
          : {};

        const updatedFormFields = (
          prev.document.tempFields.formFields || []
        ).map((field) => {
          const isTargetField = selectedFieldId
            ? field?.id === selectedFieldId
            : field?.name === selectedFieldName;

          if (
            !isTargetField &&
            (fieldKey !== "parents" ||
              !relatedOptionFields?.includes(
                selectedFieldId ? field?.id : field?.name,
              ))
          )
            return field;

          return {
            ...field,
            ...(field.type === "PDFTextField" ? { autoFontSize: true } : {}),
            [fieldKey]: value,
            ...(isTargetField ? genericCellChanges : {}),
          };
        });

        const resolvedFormFields =
          fieldKey === "name" && isGenericTableMember(selectedField)
            ? remapGenericTableFormulaReferences(
                prev.document.tempFields.formFields || [],
                updatedFormFields,
              )
            : updatedFormFields;
        const nextSelectedField = {
          ...selectedField,
          ...(selectedField?.type === "PDFTextField"
            ? { autoFontSize: true }
            : {}),
          [fieldKey]: value,
          ...genericCellChanges,
        };

        return {
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prev.document.tempFields,
              formFields: resolvedFormFields,
              selectedFormField: nextSelectedField,
            },
          },
        };
      });
    },
    [onNotify, setCurrentProcessingFile, pushHistory, combineFields],
  );

  const isBlockField = (field) => field?.name?.startsWith("block__");

  const handleSelectedFieldsGeometryChange = useCallback(
    (fieldKey, value) => {
      if (!["x", "y", "width", "height"].includes(fieldKey)) return;

      const hasValue = value !== null && value !== undefined && value !== "";
      const numericValue = hasValue ? Number(value) : "";

      if (hasValue && !Number.isFinite(numericValue)) return;

      pushHistory?.();

      setCurrentProcessingFile((prevFile) => {
        const tempFields = prevFile?.document?.tempFields;
        const selectedTextFields = (
          tempFields?.selectedExtraFields || []
        ).filter((f) => !isBlockField(f));

        if (selectedTextFields.length < 2) return prevFile;

        const selectedKeys = new Set(
          selectedTextFields.map((f) => f?.field_instance_id || f?.name),
        );
        const isSelected = (field) =>
          !isBlockField(field) &&
          selectedKeys.has(field?.field_instance_id || field?.name);

        const isSizing = fieldKey === "width" || fieldKey === "height";

        const updateField = (field) => {
          if (!isSelected(field)) return field;

          return {
            ...field,
            [fieldKey]: numericValue,
            ...(isSizing ? { autoFontSize: true } : {}),
          };
        };

        const updatedFormFields = (tempFields?.formFields || []).map(
          updateField,
        );

        const updatedByKey = new Map(
          updatedFormFields.map((field) => [
            field?.field_instance_id || field?.name,
            field,
          ]),
        );
        const pickUpdated = (field) =>
          updatedByKey.get(field?.field_instance_id || field?.name) || field;

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...tempFields,
              formFields: updatedFormFields,
              selectedExtraFields: (tempFields?.selectedExtraFields || []).map(
                pickUpdated,
              ),
              selectedFormField: tempFields?.selectedFormField
                ? pickUpdated(tempFields.selectedFormField)
                : null,
            },
          },
        };
      });
    },
    [pushHistory, setCurrentProcessingFile],
  );
  const handleSelectedFieldsAlignmentChange = useCallback(
    (alignment) => {
      if (alignment === null || alignment === undefined || alignment === "")
        return;

      const tempFields = currentProcessingFile?.document?.tempFields;
      const selectedTextFields = tempFields?.selectedExtraFields || [];

      if (selectedTextFields.length < 2) return;

      pushHistory?.();

      setCurrentProcessingFile((prevFile) => {
        const prevTempFields = prevFile?.document?.tempFields;
        const prevSelected = prevTempFields?.selectedExtraFields || [];

        if (prevSelected.length < 2) return prevFile;

        const selectedKeys = new Set(
          prevSelected.map((field) => field?.field_instance_id || field?.name),
        );
        const isSelected = (field) =>
          selectedKeys.has(field?.field_instance_id || field?.name);

        const applyAlignment = (field) =>
          isSelected(field) ? { ...field, textAlign: alignment } : field;

        const updatedFormFields = (prevTempFields?.formFields || []).map(
          applyAlignment,
        );

        const updatedByKey = new Map(
          updatedFormFields.map((field) => [
            field?.field_instance_id || field?.name,
            field,
          ]),
        );
        const pickUpdated = (field) =>
          updatedByKey.get(field?.field_instance_id || field?.name) || field;

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevTempFields,
              formFields: updatedFormFields,
              selectedExtraFields: (
                prevTempFields?.selectedExtraFields || []
              ).map(pickUpdated),
              selectedFormField: prevTempFields?.selectedFormField
                ? pickUpdated(prevTempFields.selectedFormField)
                : null,
            },
          },
        };
      });
    },
    [currentProcessingFile, pushHistory, setCurrentProcessingFile],
  );

  const handleSelectedFieldsParentChange = useCallback(
    (parents) => {
      if (!Array.isArray(parents)) return;

      const tempFields = currentProcessingFile?.document?.tempFields;
      const selectedTextFields = tempFields?.selectedExtraFields || [];

      if (selectedTextFields.length < 2) return;

      pushHistory?.();

      setCurrentProcessingFile((prevFile) => {
        const prevTempFields = prevFile?.document?.tempFields;
        const prevSelected = prevTempFields?.selectedExtraFields || [];

        if (prevSelected.length < 2) return prevFile;

        const selectedKeys = new Set(
          prevSelected.map((field) => field?.field_instance_id || field?.name),
        );
        const isSelected = (field) =>
          selectedKeys.has(field?.field_instance_id || field?.name);

        const applyParents = (field) =>
          isSelected(field) ? { ...field, parents } : field;

        const updatedFormFields = (prevTempFields?.formFields || []).map(
          applyParents,
        );

        const updatedByKey = new Map(
          updatedFormFields.map((field) => [
            field?.field_instance_id || field?.name,
            field,
          ]),
        );
        const pickUpdated = (field) =>
          updatedByKey.get(field?.field_instance_id || field?.name) || field;

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevTempFields,
              formFields: updatedFormFields,
              selectedExtraFields: (
                prevTempFields?.selectedExtraFields || []
              ).map(pickUpdated),
              selectedFormField: prevTempFields?.selectedFormField
                ? pickUpdated(prevTempFields.selectedFormField)
                : null,
            },
          },
        };
      });
    },
    [currentProcessingFile, pushHistory, setCurrentProcessingFile],
  );

  const handleSelectedFieldsLabelChange = useCallback(
    (value) => {
      if (value === null || value === undefined) return;

      const tempFields = currentProcessingFile?.document?.tempFields;
      const selectedTextFields = tempFields?.selectedExtraFields || [];

      if (selectedTextFields.length < 2) return;

      pushHistory?.();

      setCurrentProcessingFile((prevFile) => {
        const prevTempFields = prevFile?.document?.tempFields;
        const prevSelected = prevTempFields?.selectedExtraFields || [];

        if (prevSelected.length < 2) return prevFile;

        const selectedKeys = new Set(
          prevSelected.map((field) => field?.field_instance_id || field?.name),
        );
        const isSelected = (field) =>
          selectedKeys.has(field?.field_instance_id || field?.name);

        const applyLabel = (field) => {
          if (!isSelected(field)) return field;

          return {
            ...field,
            field_label: value,
            label: value,
            ...(field?.type === "PDFTextField" ? { autoFontSize: true } : {}),
          };
        };

        const updatedFormFields = (prevTempFields?.formFields || []).map(
          applyLabel,
        );

        const updatedByKey = new Map(
          updatedFormFields.map((field) => [
            field?.field_instance_id || field?.name,
            field,
          ]),
        );
        const pickUpdated = (field) =>
          updatedByKey.get(field?.field_instance_id || field?.name) || field;

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevTempFields,
              formFields: updatedFormFields,
              selectedExtraFields: (
                prevTempFields?.selectedExtraFields || []
              ).map(pickUpdated),
              selectedFormField: prevTempFields?.selectedFormField
                ? pickUpdated(prevTempFields.selectedFormField)
                : null,
            },
          },
        };
      });
    },
    [currentProcessingFile, pushHistory, setCurrentProcessingFile],
  );

  const handleMakeRowGroup = useCallback(() => {
    const tempFields = currentProcessingFile?.document?.tempFields;
    const id = Date.now();
    const selectedFieldIds = new Map();

    if (
      !tempFields?.selectedExtraFields ||
      tempFields.selectedExtraFields.length < 2
    ) {
      return;
    }

    const genericSelectedFields = tempFields.selectedExtraFields.filter(
      (field) => field?.field_id === "generic_table",
    );

    if (
      genericSelectedFields.length > 0 &&
      genericSelectedFields.length !== tempFields.selectedExtraFields.length
    ) {
      onNotify?.(
        "error",
        "Generic table fields cannot be grouped together with regular fields.",
      );
      return;
    }

    if (genericSelectedFields.length > 0) {
      const tableIds = new Set(
        genericSelectedFields.map((field) => field?.generic_table?.table_id),
      );
      const rowIndexes = new Set(
        genericSelectedFields.map((field) => field?.generic_table?.row_index),
      );
      const hasMissingGenericPosition = genericSelectedFields.some(
        (field) =>
          !field?.generic_table?.table_id ||
          !Number.isInteger(Number(field?.generic_table?.row_index)),
      );

      if (
        hasMissingGenericPosition ||
        tableIds.size !== 1 ||
        rowIndexes.size !== 1
      ) {
        onNotify?.(
          "error",
          "Select Generic table fields from the same table row to group them.",
        );
        return;
      }
    }

    tempFields?.selectedExtraFields?.forEach((f, index) => {
      selectedFieldIds.set(f?.name, `${id}_${index + 1}`);
    });

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (selectedFieldIds?.has(f?.name)) {
        return {
          ...f,
          row: selectedFieldIds?.get(f?.name),
          ...(f?.field_id === "generic_table" && f?.generic_table
            ? {
                generic_table: {
                  ...f.generic_table,
                  row_group_disabled: false,
                },
              }
            : {}),
        };
      }
      return f;
    });

    const updatedSelectedFields = tempFields?.selectedExtraFields?.map((f) => ({
      ...f,
      row: selectedFieldIds?.get(f?.name),
      ...(f?.field_id === "generic_table" && f?.generic_table
        ? {
            generic_table: {
              ...f.generic_table,
              row_group_disabled: false,
            },
          }
        : {}),
    }));

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: [...updatedFormFields],
          selectedExtraFields: updatedSelectedFields,
          selectedFormField:
            prevFile.document.tempFields.selectedFormField &&
            selectedFieldIds.has(
              prevFile.document.tempFields.selectedFormField.name,
            )
              ? {
                  ...prevFile.document.tempFields.selectedFormField,
                  row: selectedFieldIds.get(
                    prevFile.document.tempFields.selectedFormField.name,
                  ),
                  ...(prevFile.document.tempFields.selectedFormField
                    ?.field_id === "generic_table" &&
                  prevFile.document.tempFields.selectedFormField?.generic_table
                    ? {
                        generic_table: {
                          ...prevFile.document.tempFields.selectedFormField
                            .generic_table,
                          row_group_disabled: false,
                        },
                      }
                    : {}),
                }
              : prevFile.document.tempFields.selectedFormField,
        },
      },
    }));

    onNotify("success", "Selected fields have been grouped into a single row");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProcessingFile]);

  const handleRemoveFieldFromRow = useCallback(
    (row, isAll = false, displayLabel, options, genericRowGroupContext) => {
      const tempFields = currentProcessingFile?.document?.tempFields;

      if (genericRowGroupContext?.inherited) {
        const {
          tableId,
          rowIndex,
          columnIndex,
          groupedColumnIndexes = [],
        } = genericRowGroupContext;
        const normalizedGroupedColumnIndexes = groupedColumnIndexes.map(Number);
        const columnsToDisable = new Set(
          isAll || normalizedGroupedColumnIndexes.length <= 2
            ? normalizedGroupedColumnIndexes
            : [Number(columnIndex)],
        );
        const updateGenericRowGroupOverride = (field) => {
          const isTargetField =
            field?.generic_table?.table_id === tableId &&
            Number(field?.generic_table?.row_index) === Number(rowIndex) &&
            columnsToDisable.has(Number(field?.generic_table?.column_index));

          return isTargetField
            ? {
                ...field,
                row: "",
                generic_table: {
                  ...field.generic_table,
                  row_group_disabled: true,
                },
              }
            : field;
        };

        setCurrentProcessingFile((prevFile) => {
          const prevTempFields = prevFile?.document?.tempFields;
          const updatedFormFields = (prevTempFields?.formFields || []).map(
            updateGenericRowGroupOverride,
          );
          const findUpdatedField = (selectedField) =>
            updatedFormFields.find((field) =>
              selectedField?.field_instance_id
                ? field?.field_instance_id === selectedField.field_instance_id
                : field?.name === selectedField?.name,
            );

          return {
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...prevTempFields,
                formFields: updatedFormFields,
                selectedExtraFields: (
                  prevTempFields?.selectedExtraFields || []
                ).map(
                  (selectedField) =>
                    findUpdatedField(selectedField) || selectedField,
                ),
                selectedFormField:
                  findUpdatedField(prevTempFields?.selectedFormField) ||
                  prevTempFields?.selectedFormField,
              },
            },
          };
        });

        onNotify(
          "success",
          isAll || normalizedGroupedColumnIndexes.length <= 2
            ? "All fields have been removed from the row"
            : `${displayLabel} fields removed from the row`,
        );
        return;
      }

      const prefix = row?.split("_")?.[0];

      const optionsSet = options ? new Set(options) : null;

      const rowGroupFields = tempFields?.formFields?.filter((f) =>
        f?.row?.includes(prefix),
      );

      const updatedFormFields = tempFields?.formFields?.map((f) => {
        const match =
          isAll || rowGroupFields?.length <= 2
            ? f?.row?.includes(prefix)
            : row
              ? f?.row === row
              : optionsSet?.has(f?.name);

        return match ? { ...f, row: "" } : f;
      });

      const updatedSelectedFields = tempFields?.selectedExtraFields?.map(
        (f) => ({
          ...f,
          row: updatedFormFields?.find((field) => f?.name === field?.name)?.row,
        }),
      );

      setCurrentProcessingFile((prevFile) => {
        const wasSelectedFieldRemoved =
          prevFile.document.tempFields.selectedFormField &&
          (isAll
            ? prevFile.document.tempFields.selectedFormField.row?.includes(
                prefix,
              )
            : row
              ? prevFile.document.tempFields.selectedFormField.row === row
              : optionsSet?.has(
                  prevFile.document.tempFields.selectedFormField.name,
                ));

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: updatedFormFields,
              selectedExtraFields: updatedSelectedFields,
              selectedFormField: wasSelectedFieldRemoved
                ? {
                    ...prevFile.document.tempFields.selectedFormField,
                    row: "",
                  }
                : prevFile.document.tempFields.selectedFormField,
            },
          },
        };
      });

      onNotify(
        "success",
        isAll
          ? "All fields have been removed from the row"
          : `${displayLabel} fields removed from the row`,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentProcessingFile],
  );

  const handleGenericFieldsAssign = useCallback(
    (configuration) => {
      const { genericConfig } = configuration || {};
      if (!genericConfig) return;

      const isEditingExistingTable = Boolean(editingGenericTable?.tableId);
      const requestedMemberFieldKeys =
        configuration?.memberFieldKeys || genericConfig.memberFieldKeys;

      const requestedMemberKeySet = Array.isArray(requestedMemberFieldKeys)
        ? new Set(requestedMemberFieldKeys)
        : null;

      const detectedSourceRows = isEditingExistingTable
        ? editingGenericTable.rows
        : paisSelectedTableRows;

      const detectedSourceColumnCount = detectedSourceRows.reduce(
        (max, row) => Math.max(max, row.fields.length),
        0,
      );
      const requestedColumnCount = genericConfig.columnCount;

      const shouldRegroup =
        !isEditingExistingTable &&
        Number.isInteger(requestedColumnCount) &&
        requestedColumnCount >= 1 &&
        requestedColumnCount !== detectedSourceColumnCount;

      const sourceRows = shouldRegroup
        ? (() => {
            // Column-major reading order: left column top to bottom, then the next column.
            const ordered = [];
            for (let col = 0; col < detectedSourceColumnCount; col++) {
              detectedSourceRows.forEach((row) => {
                if (row.fields[col]) ordered.push(row.fields[col]);
              });
            }

            const regrouped = [];
            for (let i = 0; i < ordered.length; i += requestedColumnCount) {
              regrouped.push({
                fields: ordered.slice(i, i + requestedColumnCount),
              });
            }
            return regrouped;
          })()
        : detectedSourceRows;

      const sourceColumnCount = isEditingExistingTable
        ? editingGenericTable.config.columnCount
        : sourceRows.reduce((max, row) => Math.max(max, row.fields.length), 0);

      const keptColumnIndices =
        isEditingExistingTable && genericConfig.keptColumnIndices !== undefined
          ? genericConfig.keptColumnIndices
          : Array.from({ length: sourceColumnCount }, (_, index) => index);

      const validColumnSelection =
        Array.isArray(keptColumnIndices) &&
        keptColumnIndices.length > 0 &&
        new Set(keptColumnIndices).size === keptColumnIndices.length &&
        keptColumnIndices.every(
          (index) =>
            Number.isInteger(index) && index >= 0 && index < sourceColumnCount,
        );

      if (!validColumnSelection) {
        onNotify?.("error", "The selected table columns are invalid.");
        return;
      }

      // Original column index -> new column index after removal.
      const newColumnIndexByOriginal = new Map(
        keptColumnIndices.map((originalIndex, newIndex) => [
          originalIndex,
          newIndex,
        ]),
      );

      const columnIndexByFieldKey = new Map();

      const rows = sourceRows
        .map((row) => ({
          ...row,
          fields: row.fields.filter((field, fieldIndex) => {
            const fieldKey = getEditorFieldKey(field);

            if (
              isEditingExistingTable &&
              requestedMemberKeySet &&
              !requestedMemberKeySet.has(fieldKey)
            ) {
              return false;
            }

            const originalColumnIndex = isEditingExistingTable
              ? (editingGenericTable.columnIndexByFieldKey?.get(fieldKey) ??
                fieldIndex)
              : fieldIndex;

            const newColumnIndex =
              newColumnIndexByOriginal.get(originalColumnIndex);

            if (newColumnIndex === undefined) return false;

            columnIndexByFieldKey.set(fieldKey, newColumnIndex);
            return true;
          }),
        }))
        .filter((row) => row.fields.length > 0);

      const selected = rows.flatMap((row) => row.fields);

      if (!selected.length || !rows.length) return;

      const {
        label,
        rowMode,
        headerMode,
        rowCount,
        maxRowCount,
        columnCount,
        rowNames = [],
        columnNames = [],
        columnTypes = [],
      } = genericConfig;
      // Incoming source indices use the final (post-removal) column order.
      const columnTotalSources =
        genericConfig.columnTotalSources ??
        keptColumnIndices.map((originalIndex) =>
          (
            editingGenericTable?.config?.columnTotalSources?.[originalIndex] ||
            []
          )
            .map((index) => newColumnIndexByOriginal.get(index))
            .filter((index) => Number.isInteger(index)),
        );
      const hasValidTotalSources =
        Array.isArray(columnTotalSources) &&
        columnTotalSources.length === columnCount &&
        columnTotalSources.every((sources, columnIndex) =>
          columnTypes[columnIndex] !== "total"
            ? true
            : Array.isArray(sources) &&
              sources.length > 0 &&
              new Set(sources).size === sources.length &&
              sources.every(
                (index) =>
                  Number.isInteger(index) &&
                  index >= 0 &&
                  index < columnCount &&
                  index !== columnIndex &&
                  columnTypes[index] !== "total",
              ),
        );
      const detectedColumnCount = rows.reduce(
        (max, row) => Math.max(max, row.fields?.length || 0),
        0,
      );
      const hasNamedRows = headerMode === "named_rows";
      const isCustomRowTable = rowMode === "custom";
      const hasValidMaximum =
        maxRowCount === null ||
        maxRowCount === undefined ||
        (Number.isInteger(maxRowCount) && maxRowCount >= rows.length);
      const isValidConfiguration =
        label &&
        Number.isInteger(rowCount) &&
        rowCount === rows.length &&
        hasValidMaximum &&
        (!isCustomRowTable || !hasNamedRows) &&
        Number.isInteger(columnCount) &&
        columnCount ===
          (isEditingExistingTable
            ? keptColumnIndices.length
            : detectedColumnCount) &&
        columnNames.length === columnCount &&
        columnNames.every(Boolean) &&
        columnTypes.length === columnCount &&
        columnTypes.every((type) =>
          ["text", "number", "total"].includes(type),
        ) &&
        hasValidTotalSources &&
        (!hasNamedRows ||
          (rowNames.length === rowCount && rowNames.every(Boolean)));

      if (!isValidConfiguration) {
        onNotify?.(
          "error",
          rowCount < rows.length
            ? `The configured row count must be at least ${rows.length} to cover the selected fields.`
            : "The generic table configuration does not match the selected fields.",
        );
        return;
      }

      const toTagSegment = (value, fallback) => {
        const normalized = String(value || "")
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "");

        return normalized || fallback;
      };
      const tableId = isEditingExistingTable
        ? editingGenericTable.tableId
        : `generic_${toTagSegment(label, "table")}_${Date.now()}`;
      const tagByFieldKey = new Map();

      rows.forEach((row, rowIndex) => {
        row.fields.forEach((field) => {
          const fieldKey = getEditorFieldKey(field);
          const columnIndex = columnIndexByFieldKey.get(fieldKey);
          const rowName = hasNamedRows ? rowNames[rowIndex] : null;
          const columnName = columnNames[columnIndex];
          const columnType = columnTypes[columnIndex];
          const previousMetadata = field?.generic_table;
          const fieldTypeOverride = previousMetadata?.field_type_override;
          const hasFieldTypeOverride = ["text", "number", "total"].includes(
            fieldTypeOverride,
          );
          const fieldType = hasFieldTypeOverride
            ? fieldTypeOverride
            : columnType;
          const rowTag = hasNamedRows
            ? toTagSegment(rowName, `row_${rowIndex + 1}`)
            : `row_${rowIndex + 1}`;
          const columnTag = toTagSegment(
            columnName,
            `column_${columnIndex + 1}`,
          );

          tagByFieldKey.set(fieldKey, {
            name: `${tableId}_r${rowIndex + 1}_c${columnIndex + 1}_${rowTag}_${columnTag}`,
            displayLabel: hasNamedRows
              ? [label, rowName, columnName].filter(Boolean).join(" - ")
              : [label, columnName, rowIndex + 1].join(" - "),
            metadata: {
              table_id: tableId,
              table_label: label,
              row_mode: rowMode,
              header_mode: headerMode,
              row_count: rowCount,
              max_row_count: isCustomRowTable ? (maxRowCount ?? null) : null,
              column_count: columnCount,
              row_index: rowIndex,
              column_index: columnIndex,
              row_name: rowName,
              column_name: columnName,
              field_type: fieldType,
              column_type: columnType,
              column_total_sources:
                columnType === "total" ? columnTotalSources[columnIndex] : [],
              ...(hasFieldTypeOverride
                ? { field_type_override: fieldTypeOverride }
                : {}),
              ...(previousMetadata?.formula_override
                ? { formula_override: true }
                : {}),
              ...(field?.generic_table?.original_name
                ? {
                    original_name: field.generic_table.original_name,
                    original_field_label:
                      field.generic_table.original_field_label,
                    original_label: field.generic_table.original_label,
                    original_field_id: field.generic_table.original_field_id,
                    original_field_type:
                      field.generic_table.original_field_type,
                  }
                : !field?.generic_table
                  ? {
                      original_name: field?.name,
                      original_field_label: field?.field_label,
                      original_label: field?.label,
                      original_field_id: field?.field_id,
                      original_field_type: field?.field_type,
                    }
                  : {}),
            },
            fieldType,
            ...(previousMetadata?.column_type === "total" &&
            !previousMetadata?.formula_override &&
            fieldType !== "total"
              ? { formulaFields: [] }
              : {}),
          });
        });
      });

      if (tagByFieldKey.size !== selected.length) {
        onNotify?.(
          "error",
          "Could not map every selected field to the generic table.",
        );
        return;
      }

      // Build formulas only after every cell has its final name and position.
      rows.forEach((row) => {
        const rowTagsByColumn = new Map(
          row.fields.map((field) => {
            const tag = tagByFieldKey.get(getEditorFieldKey(field));
            return [tag.metadata.column_index, tag];
          }),
        );

        row.fields.forEach((field) => {
          const tag = tagByFieldKey.get(getEditorFieldKey(field));
          const metadata = tag.metadata;
          if (
            tag.fieldType !== "total" ||
            metadata.column_type !== "total" ||
            metadata.formula_override
          ) {
            return;
          }

          tag.formulaFields = metadata.column_total_sources.map(
            (sourceColumnIndex, index) => {
              const sourceTag = rowTagsByColumn.get(sourceColumnIndex);

              return {
                ...(sourceTag
                  ? {
                      field: {
                        name: sourceTag.name,
                        field_label: sourceTag.displayLabel,
                      },
                    }
                  : { field: "", value: 0 }),
                ...(index > 0
                  ? { operator: { value: "+", label: "+ Add" } }
                  : {}),
              };
            },
          );
        });
      });

      pushHistory?.();

      setCurrentProcessingFile((prevFile) => {
        const prevTempFields = prevFile?.document?.tempFields;
        const existingTableId = editingGenericTable?.tableId;
        const updatedFormFields = (prevTempFields?.formFields || []).map(
          (field) => {
            const fieldKey = getEditorFieldKey(field);
            const genericTag = tagByFieldKey.get(fieldKey);
            const isRemovedTableMember =
              isEditingExistingTable &&
              field?.generic_table?.table_id === existingTableId &&
              !genericTag;

            if (isRemovedTableMember) {
              const previousMetadata = field.generic_table || {};
              const { generic_table: _genericTable, ...fieldWithoutTable } =
                field;

              return {
                ...fieldWithoutTable,
                ...(previousMetadata.original_name
                  ? { name: previousMetadata.original_name }
                  : {}),
                ...(previousMetadata.original_field_label !== undefined
                  ? { field_label: previousMetadata.original_field_label }
                  : {}),
                ...(previousMetadata.original_label !== undefined
                  ? { label: previousMetadata.original_label }
                  : {}),
                field_id: previousMetadata.original_field_id || "",
                field_type:
                  previousMetadata.original_field_type || field.field_type,
                row: "",
              };
            }

            if (!genericTag) return field;

            return {
              ...field,
              name: genericTag.name,
              field_label: genericTag.displayLabel,
              label: genericTag.displayLabel,
              field_id: "generic_table",
              field_type: genericTag.fieldType,
              generic_table: genericTag.metadata,
              ...(genericTag.formulaFields
                ? { formulaFields: genericTag.formulaFields }
                : {}),
            };
          },
        );

        const resolvedFormFields = remapGenericTableFormulaReferences(
          prevTempFields?.formFields || [],
          updatedFormFields,
        );

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevTempFields,
              formFields: resolvedFormFields,
              selectedFormField: null,
              selectedExtraFields: [],
            },
          },
        };
      });

      onNotify?.(
        "success",
        isEditingExistingTable
          ? "Generic table updated successfully"
          : "Generic table fields tagged successfully",
      );
    },
    [
      currentProcessingFile?.document?.tempFields?.currentDocument,
      editingGenericTable,
      onNotify,
      paisSelectedTableRows,
      pushHistory,
      selectedTableRows.fields,
      setCurrentProcessingFile,
    ],
  );

  const handlePaisFieldsAssign = useCallback(
    ({ category, columnOrder = [], subCategoryOrder = [] }) => {
      const selected = selectedTableRows.fields;

      if (!category || selected.length === 0) return;

      const rows = paisSelectedTableRows;
      const dynamicColumnCodeMap = columnOrder.reduce((acc, column, index) => {
        const fixedCode = PAIS_COLUMN_DEFINITIONS[index]?.code;
        if (fixedCode && column?.label) {
          acc[column.label] = fixedCode;
        }
        return acc;
      }, {});
      const dynamicSubCategoryCodeMap = subCategoryOrder.reduce(
        (acc, subCategory, index) => {
          const fixedCode = category.subCategory?.[index]?.code;
          if (fixedCode && subCategory?.label) {
            acc[subCategory.label] = fixedCode;
          }
          return acc;
        },
        {},
      );
      const overflowRowCount = Math.max(
        0,
        rows.length - (category.subCategory?.length || 0),
      );

      const tagByFieldKey = new Map();
      const skipped = [];

      rows.forEach((row, rowIndex) => {
        const detectedSubCategoryMeaning =
          category.subCategory?.[rowIndex]?.label;
        const subCategoryCode = detectedSubCategoryMeaning
          ? dynamicSubCategoryCodeMap[detectedSubCategoryMeaning]
          : undefined;
        if (!subCategoryCode) {
          skipped.push(...row.fields);
          return;
        }
        row.fields.forEach((field, colIndex) => {
          const detectedColumnMeaning =
            PAIS_COLUMN_DEFINITIONS[colIndex]?.label;
          const columnCode = detectedColumnMeaning
            ? dynamicColumnCodeMap[detectedColumnMeaning]
            : undefined;
          if (!columnCode) {
            skipped.push(field);
            return;
          }
          const key = field?.field_instance_id || field?.id || field?.name;
          tagByFieldKey.set(
            key,
            buildPaisFieldTag(
              category.categoryCode,
              subCategoryCode,
              columnCode,
            ),
          );
        });
      });

      if (tagByFieldKey.size === 0) {
        onNotify?.(
          "error",
          "Could not map the selection to PAIS categories. Try selecting fewer rows/columns.",
        );
        return;
      }

      setSavedPaisColumnOrder(columnOrder);

      pushHistory?.();

      setCurrentProcessingFile((prevFile) => {
        const prevTempFields = prevFile?.document?.tempFields;
        const allFields = prevTempFields?.formFields || [];
        const selectedKeys = new Set(tagByFieldKey.keys());
        const takenTags = new Set(
          allFields
            .filter((f) => {
              const key = f?.field_instance_id || f?.id || f?.name;
              return !selectedKeys.has(key);
            })
            .map((f) => f?.name),
        );

        const updatedFormFields = allFields.map((field) => {
          const key = field?.field_instance_id || field?.id || field?.name;
          const newTag = tagByFieldKey.get(key);
          if (!newTag) return field;
          if (takenTags.has(newTag)) {
            skipped.push(field);
            return field;
          }
          return {
            ...field,
            name: newTag,
            field_label: newTag,
            label: newTag,
            field_id: "pais",
          };
        });

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevTempFields,
              formFields: updatedFormFields,
              selectedFormField: null,
              selectedExtraFields: [],
            },
          },
        };
      });

      onNotify?.(
        skipped.length > 0 ? "error" : "success",
        skipped.length > 0
          ? overflowRowCount > 0
            ? `${skipped.length} field(s) skipped. ${category.categoryLabel} supports ${category.subCategory?.length || 0} row(s), so ${overflowRowCount} extra selected row(s) could not be tagged.`
            : `${skipped.length} field(s) skipped (name already taken or mapping unavailable).`
          : "PAIS fields tagged successfully",
      );
    },
    [
      onNotify,
      paisSelectedTableRows,
      pushHistory,
      setSavedPaisColumnOrder,
      selectedTableRows.fields,
      setCurrentProcessingFile,
    ],
  );

  const handleTableFieldsAssign = useCallback(
    (configuration) => {
      if (configuration?.tableType === "generic") {
        handleGenericFieldsAssign(configuration);
        return;
      }

      handlePaisFieldsAssign(configuration);
    },
    [handleGenericFieldsAssign, handlePaisFieldsAssign],
  );

  if (!isOpen || !formData) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      {!finalizedEditMode && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
          tabIndex={0}
        >
          <PDFEditorHeader
            title={editableTitle}
            onTitleChange={setEditableTitle}
            onSave={handleSave}
            onClose={handleClose}
            onFinalizeSuccess={onFinalizeSuccess}
            loading={editorBusy}
            setOpenPreviewDialog={setOpenPreviewDialog}
            formData={formData}
            isNew={isNew}
            openSettingsDialog={openSettingsDialog}
            setOpenSettingsDialog={setOpenSettingsDialog}
            onUpdate={handleUpdateSettings}
            editLockLost={editLockLost}
          />

          {editLockLost && (
            <Alert severity="error" variant="filled" sx={{ borderRadius: 0 }}>
              This editor no longer owns the inactive-form lock. Saving and
              editing are disabled. Close this editor and reopen the form to
              continue.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flex: 1,
              overflow: "hidden",
              pointerEvents: editLockLost ? "none" : "auto",
              opacity: editLockLost ? 0.72 : 1,
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f5f5f5",
                overflow: "hidden",
              }}
            >
              <Box sx={{ flexShrink: 0, minWidth: 0 }}>
                <PDFEditorControlsBar
                  activeTool={activeEditorTool}
                  onToolSelect={handleEditorToolSelection}
                  scale={scale}
                  handleScaleChange={handleScaleChange}
                  highlightFields={highlightFields}
                  setHighlightFields={setHighlightFields}
                  textOverlayDisplayMode={textOverlayDisplayMode}
                  setTextOverlayDisplayMode={setTextOverlayDisplayMode}
                  numPages={numPages}
                  pageNumber={pageNumber}
                  handlePageChange={handlePageChange}
                  objectInspectorOpen={objectInspectorOpen}
                  onToggleObjectInspector={handleToggleObjectInspector}
                  addFieldOpen={addFieldOpen}
                  onToggleAddField={handleToggleAddField}
                  blockPanelOpen={blockPanelOpen}
                  onToggleBlockPanel={() => setBlockPanelOpen((prev) => !prev)}
                  undoRedoStats={{ undo, redo, canRedo, canUndo }}
                  textSelectionMode={textSelectionMode}
                  setTextSelectionMode={setTextSelectionMode}
                  loading={editorBusy}
                  onAutoTagFields={handleAutoTagFields}
                  handleMakeRowGroup={handleMakeRowGroup}
                  selectedFieldsCount={
                    currentProcessingFile?.document?.tempFields
                      ?.selectedExtraFields?.length || 0
                  }
                  showPaisTableButton={showPaisTableButton}
                  paisTableSelectActive={paisPopoverOpen}
                  onTogglePaisTableSelect={handleOpenPaisPopover}
                  favouriteFields={fields}
                  onFavouriteSelect={handleFavouriteFieldSelect}
                  favouriteSignerContext={favouriteSignerContext}
                  currentSession={currentSession}
                  onNotify={onNotify}
                  textConnectionControl={
                    <FieldConnector
                      currentProcessingFile={currentProcessingFile}
                      setCurrentProcessingFile={setCurrentProcessingFile}
                      catalog={fields}
                      pushHistory={pushHistory}
                      disabled={editorBusy || activeEditorTool !== "edit"}
                      onNotify={onNotify}
                    />
                  }
                  canUnassignSelectedFields={canUnassignSelectedFields}
                  onUnassignSelectedFields={handleUnassignSelectedFields}
                  parcelAutoTagControl={
                    <ParcelAutoTagControl
                      currentProcessingFile={currentProcessingFile}
                      setCurrentProcessingFile={setCurrentProcessingFile}
                      catalog={fields}
                      pushHistory={pushHistory}
                      disabled={loading || activeEditorTool !== "edit"}
                      onNotify={onNotify}
                    />
                  }
                />
              </Box>

              {showPaisTableButton && (
                <PaisTagAssignPopover
                  open={paisPopoverOpen}
                  onClose={() => setPaisPopoverOpen(false)}
                  categoryData={paisCategoryData}
                  detectedColumnCount={
                    editingGenericTable?.config?.columnCount ||
                    paisDetectedColumnCount
                  }
                  detectedRowCount={
                    editingGenericTable?.rows?.length ||
                    paisSelectedTableRows.length
                  }
                  columnDefinitions={PAIS_COLUMN_DEFINITIONS}
                  savedColumnOrder={savedPaisColumnOrder}
                  supportsPaisTableTagging={
                    tableTagSelectionMode.supportsPaisTableTagging
                  }
                  supportsGenericTableTagging={
                    tableTagSelectionMode.supportsGenericTableTagging
                  }
                  selectedCount={
                    editingGenericTable?.fields?.length ||
                    selectedTableRows.fields.length ||
                    0
                  }
                  editingGenericTable={editingGenericTable}
                  pdfPreview={
                    <TableTagPdfPreview
                      file={base64File || pdfInput}
                      initialPage={pageNumber}
                      displayRotation={displayRotation}
                    />
                  }
                  onAssign={handleTableFieldsAssign}
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <PDFEditorLeftPanel
                  isOpen={blockPanelOpen}
                  currentProcessingFile={currentProcessingFile}
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  fieldNavigationTarget={fieldNavigationTarget}
                  onFieldNavigationComplete={handleFieldNavigationComplete}
                  onNotify={onNotify}
                  loading={editorBusy}
                  fields={fields}
                  getFormFieldsList={getFormFields}
                />

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    overflow: "hidden",
                    paddingBottom: "60px",
                    boxSizing: "border-box",
                  }}
                >
                  <PDFCanvas
                    base64File={base64File}
                    pdfInput={pdfInput}
                    pdfScrollContainerRef={pdfScrollContainerRef}
                    pdfContainerRef={pdfContainerRef}
                    pdfDocumentRef={pdfDocumentRef}
                    currentProcessingFile={currentProcessingFile}
                    setCurrentProcessingFile={setCurrentProcessingFile}
                    pageNumber={pageNumber}
                    scale={scale}
                    displayRotation={displayRotation}
                    selectToolActive={selectToolActive}
                    typingToolActive={typingToolActive}
                    highlightFields={highlightFields}
                    textOverlayDisplayMode={textOverlayDisplayMode}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                    currCursor={currCursor}
                    unassignedFieldNames={unassignedFieldNames}
                    fixedInputs={fixedInputs}
                    inputRef={inputRef}
                    childInputRef={childInputRef}
                    resizing={resizing}
                    setResizing={setResizing}
                    moving={moving}
                    setMoving={setMoving}
                    isDrawing={isDrawing}
                    setIsDrawing={setIsDrawing}
                    isSelecting={isSelecting}
                    setIsSelecting={setIsSelecting}
                    rect={rect}
                    setRect={setRect}
                    selectionRect={selectionRect}
                    setSelectionRect={setSelectionRect}
                    freeSelectStartPoint={freeSelectStartPoint}
                    setFreeSelectStartPoint={setFreeSelectStartPoint}
                    startPoint={startPoint}
                    setStartPoint={setStartPoint}
                    initialMousePosition={initialMousePosition}
                    setInitialMousePosition={setInitialMousePosition}
                    initialInputSize={initialInputSize}
                    setInitialInputSize={setInitialInputSize}
                    inputPosition={inputPosition}
                    setInputPosition={setInputPosition}
                    resizeDirection={resizeDirection}
                    setResizeDirection={setResizeDirection}
                    setCurrCursor={setCurrCursor}
                    onDocumentLoadSuccess={onDocumentLoadSuccess}
                    onPageLoadSuccess={onPageLoadSuccess}
                    handleFieldChange={handleFieldChange}
                    undoRedoStats={{ undo, redo, pushHistory }}
                    activeEditorTool={activeEditorTool}
                    handleEditorToolSelection={handleEditorToolSelection}
                    textSelectionMode={textSelectionMode}
                    formFields={fields}
                    onNavigateToTaggedField={handleNavigateToTaggedField}
                    onSearchSelectedText={handleSearchSelectedText}
                    onFavouriteDrop={handleFavouriteDrop}
                    favouriteDropDisabled={editorBusy}
                  />
                </Box>

                <AddFieldDrawer
                  isOpen={addFieldOpen}
                  currentProcessingFile={currentProcessingFile}
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  currCursor={currCursor}
                  formFields={fields}
                  onToggleObjectInspector={() => {
                    if (addFieldOpen && !objectInspectorOpen) {
                      setAddFieldOpen(false);
                    }
                    setObjectInspectorOpen((prev) => !prev);
                  }}
                />

                <ObjectInspectorDrawer
                  isOpen={objectInspectorOpen}
                  selectedField={
                    currentProcessingFile?.document?.tempFields
                      ?.selectedFormField
                  }
                  selectedTextItem={
                    currentProcessingFile?.document?.tempFields
                      ?.selectedPdfTextItem
                  }
                  pdfTextEdits={
                    currentProcessingFile?.document?.tempFields?.pdfTextEdits ||
                    []
                  }
                  allFields={combineFields}
                  prefillSourceFields={fields}
                  prefillFormId={prefillFormId}
                  onPrefillChange={handleObjectInspectorPrefillChange}
                  documentFields={
                    currentProcessingFile?.document?.tempFields?.formFields ||
                    []
                  }
                  currentDocument={
                    currentProcessingFile?.document?.tempFields?.currentDocument
                  }
                  onFieldChange={handleObjectInspectorFieldChange}
                  loading={editorBusy}
                  textOverlayDisplayMode={textOverlayDisplayMode}
                  handleRemoveFieldFromRow={handleRemoveFieldFromRow}
                  selectedFields={
                    currentProcessingFile?.document?.tempFields
                      ?.selectedExtraFields || []
                  }
                  onSelectedFieldsGeometryChange={
                    handleSelectedFieldsGeometryChange
                  }
                  onSelectedFieldsAlignmentChange={
                    handleSelectedFieldsAlignmentChange
                  }
                  onSelectedFieldsParentChange={
                    handleSelectedFieldsParentChange
                  }
                  onSelectedFieldsLabelChange={handleSelectedFieldsLabelChange}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {openPreviewDialog && (
        <PreviewFormDialog
          open={openPreviewDialog}
          setOpen={
            finalizedEditMode
              ? (nextOpen, closeOptions = {}) => {
                  if (!nextOpen) {
                    if (closeOptions.saved) {
                      onClose?.(false);
                    } else {
                      handleClose();
                    }
                  }
                }
              : setOpenPreviewDialog
          }
          base64File={base64File}
          pdfInput={pdfInput}
          currentProcessingFile={currentProcessingFile}
          setCurrentProcessingFile={setCurrentProcessingFile}
          pageNumber={pageNumber}
          displayRotation={displayRotation}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          onPageLoadSuccess={onPageLoadSuccess}
          titleMetadata={{
            title: editableTitle,
            pageNumber: pageNumber,
            numPages: numPages,
            county:
              formData?.verified_details?.county ?? formData?.county ?? "",
            municipality:
              formData?.verified_details?.municipality ??
              formData?.municipality ??
              "",
          }}
          fields={fields}
          numPages={numPages}
          handlePageChange={handlePageChange}
          finalizedEditMode={finalizedEditMode}
          onSave={finalizedEditMode ? () => handleSave(false) : undefined}
        />
      )}
      {openSettingsDialog && (
        <FormDetailsCard
          selectedFile={formData}
          onSubmit={handleUpdateSettings}
          onCancel={
            finalizedEditMode ? handleClose : () => setOpenSettingsDialog(false)
          }
          uploading={isLoadingUpdateSetting}
          jurisdictionOptions={jurisdictionOptions || undefined}
          initialValues={{
            ...(formData?.updated_settings || formData?.guessedFormDetails),
            docSize:
              formData?.updated_settings?.docSize ||
              formData?.guessedFormDetails?.docSize ||
              formData?.form?.doc?.size,
            documentLabel:
              editableTitle || formData?.formName || formData?.fileName || "",
          }}
          isAlreadyUploaded={true}
          finalizedEditMode={finalizedEditMode}
        />
      )}
    </DndProvider>
  );
};

export default PDFFormEditor;
